/**
 * Экран «Закупка»: режимы, поля шапки и столбцы таблицы ввода
 * (перенос описания листа «Импорт Закупки» из Config.gs 3.13.2, тот же порядок столбцов).
 */
/**
 * Режимы экрана «Закупка». Режим «Упаковка» (packing) сохранён в коде расчётов и на сервере,
 * но убран из интерфейса по решению от сентября 2026 — вместе с экраном «Склад».
 */
/**
 * Экран «Закупка» — одна таблица (режим purchase): наличие у фабрики, цена и «Закупить» в одной строке.
 * Режим stock остаётся внутри программы — по нему строится файл «Выгрузить для фабрики».
 */
export const PURCHASE_MODES = [{ id: 'purchase', title: 'Закупка' }];
const MODE_TITLES = {
    stock: 'Наличие и цены фабрики',
    purchase: 'Закупка',
    packing: 'Упаковка'
};
export function modeTitle(mode) {
    return MODE_TITLES[mode];
}
/** Значение «все открытые запросы» в выборе запроса. */
export const REQ_ALL = '*';
export const PURCHASE_COLUMNS = [
    { key: 'art', title: 'OEM', width: 170, format: 'text' },
    { key: 'name', title: 'Наименование', width: 240, format: 'text', ref: true },
    { key: 'need', title: 'Требуется, шт', width: 100, format: 'int', ref: true, modes: ['stock', 'purchase'] },
    { key: 'boughtSoFar', title: 'Уже закуплено, шт', width: 110, format: 'int', ref: true, modes: ['purchase', 'packing'] },
    { key: 'stock', title: 'Наличие у фабрики, шт', width: 120, format: 'int', modes: ['stock', 'purchase'], required: ['stock'], optional: ['purchase'] },
    { key: 'stockDate', title: 'Дата наличия', width: 105, format: 'text', ref: true, modes: ['purchase'] },
    {
        key: 'price',
        title: 'Цена фабрики, CNY',
        width: 120,
        format: 'money',
        modes: ['stock', 'purchase'],
        required: ['stock'],
        optional: ['purchase']
    },
    { key: 'bought', title: 'Закупить, шт', width: 110, format: 'int', modes: ['purchase'], optional: ['purchase'] },
    { key: 'place', title: '№ партии', width: 100, format: 'text', modes: ['packing'], required: ['packing'] },
    { key: 'box', title: '№ коробки', width: 100, format: 'text', modes: ['packing'], required: ['packing'] },
    { key: 'net', title: 'Вес нетто, кг', width: 105, format: 'weight', modes: ['packing'], optional: true },
    { key: 'gross', title: 'Вес брутто, кг', width: 105, format: 'weight', modes: ['packing'], optional: true },
    { key: 'marking', title: 'Маркировка', width: 130, format: 'text', modes: ['packing'], optional: true },
    { key: 'series', title: 'Серия', width: 100, format: 'text', modes: ['packing'], optional: true },
    { key: 'maker', title: 'Производитель', width: 140, format: 'text', modes: ['packing'], optional: true },
    { key: 'material', title: 'Материал', width: 120, format: 'text', modes: ['packing'], optional: true },
    { key: 'note', title: 'Примечание', width: 180, format: 'text', modes: ['packing'], optional: true },
    { key: 'hs', title: 'HS code', width: 120, format: 'text', optional: true, required: ['stock'], soft: true },
    { key: 'suggestion', title: 'OEM от закупки', width: 150, format: 'text', modes: ['stock', 'purchase'], optional: ['purchase'] }
];
export function isVisible(col, mode) {
    return !col.modes || col.modes.includes(mode);
}
export function isRequired(col, mode) {
    return col.required === true || (Array.isArray(col.required) && col.required.includes(mode));
}
export function isOptional(col, mode) {
    return col.optional === true || (Array.isArray(col.optional) && col.optional.includes(mode));
}
export function visibleColumns(mode) {
    return PURCHASE_COLUMNS.filter((c) => isVisible(c, mode));
}
/** Столбцы ввода текущего режима в порядке показа (в них идёт вставка из Excel; справочные пропускаются). */
export function inputColumns(mode) {
    return visibleColumns(mode).filter((c) => !c.ref);
}
export function headerFieldTexts(mode) {
    const reqHints = {
        stock: 'Выберите запрос — OEM подставятся сами. «Все открытые запросы» — вся текущая потребность',
        purchase: 'Выберите запрос — подставятся OEM, которые ещё нужно закупить, с последним наличием и ценой. «Все открытые запросы» — вся текущая потребность',
        packing: 'Выберите запрос — подставятся его закупленные и ещё не упакованные OEM. «Все открытые запросы» — всё, что ждёт упаковки'
    };
    return {
        reqSel: { label: '№ запроса', hint: reqHints[mode], required: false },
        date: { label: 'Дата операции', hint: 'Дата проверки наличия или закупки (пусто — сегодня)', required: false },
        supplier: {
            label: 'Поставщик',
            hint: mode === 'packing' ? 'Необязательно' : 'Фабрика или поставщик',
            required: false
        },
        invoice: {
            label: mode === 'packing' ? '№ инвойса / упак. листа' : '№ счёта',
            hint: mode === 'packing'
                ? 'Если такой инвойс уже есть — строки добавятся к нему'
                : mode === 'purchase'
                    ? 'Номер счёта фабрики'
                    : 'Необязательно',
            required: mode === 'packing'
        },
        comment: { label: 'Комментарий', hint: 'Необязательно', required: false }
    };
}
/** Инструкция к режиму. */
export function modeInstructions(mode) {
    if (mode === 'stock') {
        return [
            'Выберите запрос — OEM подставятся автоматически. «Требуется» — сколько ещё нужно закупить.',
            'Для каждой строки обязательны наличие у фабрики и цена; HS code желательно заполнить (подставляется, если уже вносился).',
            'Если OEM нужно изменить — укажите правильный в столбце «OEM от закупки»: отдел продаж получит уведомление.',
            'Строки без наличия и цены не проводятся.'
        ];
    }
    if (mode === 'purchase') {
        return [
            'Выберите запрос — подставятся OEM, которые ещё нужно закупить. Наличие у фабрики, цена, HS code и «OEM от закупки» подтягиваются из прошлых проведений.',
            'Можно заполнить только наличие и цену — они сохранятся, и в следующий раз останется указать «Закупить». Можно сразу и то и другое.',
            'Проводятся только строки, где что-то изменилось: новое наличие, цена, HS code, «OEM от закупки» или заполнено «Закупить».',
            'Выбран конкретный запрос — закупка закрепляется за ним и не может быть больше, чем нужно по нему. «Все открытые запросы» — закупка распределяется от старых запросов к новым.',
            'Нельзя закупить больше, чем есть у фабрики: берётся введённое в строке наличие, а если его не меняли — последнее известное.'
        ];
    }
    return [
        'Укажите № инвойса / упаковочного листа в шапке, а в строках — № партии и № коробки.',
        'Указанная коробка означает, что OEM упакован в неё полностью: всё закупленное и ещё не упакованное количество (столбец «Уже закуплено»). Каждый OEM — одной строкой.',
        'Веса, маркировка, серия, производитель, HS code и материал необязательны: HS code подставляется из закупки, остальное — из прошлой упаковки.',
        'Строки без партии и коробки не проводятся.'
    ];
}
//# sourceMappingURL=columns.js.map